// NextStepsContext.jsx
import React, { createContext, useContext, useState } from 'react';
import { useAuth } from '../../utils/AuthContext/AuthContext';
import config from '../../utils/config';

const NextStepsContext = createContext();

export const NextStepsProvider = ({ children }) => {
    const { token, user } = useAuth();
    const getUserId = (user) => user.id || user._id; 

  const initialState = {
    depositEarnestMoney: { 
        state: 'Not Started',
        subSteps: {
            depositYourEarnestMoney: { completed: false },
        },
     },
    lender: { 
      state: 'Not Started',
      subSteps: {
        reviewLoanEstimate: { completed: false },
        submitLenderDocuments: { completed: false },
      }
    },
    inspection: {
      state: 'Not Started',
      subSteps: {
        additionalInspectionInfo: { completed: false },
        scheduleInspector: { completed: false, scheduleDate: null },
        uploadReport: { completed: false, uploadedAt: null },
        generateInspectionObjection: { completed: false, objectionData: null },
        negotiateRepairs: { completed: false },
        postInspectionObjection: { completed: false, decision: null },
      }
    },
    title: { 
        state: 'Not Started',
        subSteps: {
          titleUpload: { completed: false, uploadedAt: null },
          titleInsurance: { completed: false },
          titleSurveyReview: { completed: false },
          generateTitleObjection: { completed: false, objectionData: null },
          postTitleObjection: { completed: false, decision: null },
        },
      },
      insurance: { 
        state: 'Not Started',
        subSteps: {
          orderInsurance: { completed: false, contactInfo: null },
        },
      },
      appraisal: { 
        state: 'Not Started',
        subSteps: {
          scheduleAppraisal: { completed: false, scheduleDate: null },
          uploadAppraisal: { completed: false, uploadedAt: null },
          generateAppraisalObjection: { completed: false, objectionData: null },
          postAppraisalObjection: { completed: false, decision: null },
        },
      },
      utilities: { 
        state: 'Not Started',
        subSteps: {
          utilitiesAdditionalInfo: { completed: false, info: null },
        },
      },
      closing: { 
        state: 'Not Started',
        subSteps: {
          closingAdditionalInfo: { completed: false, info: null },
          scheduleClosing: { completed: false, scheduleDate: null },
        },
      },
      walkthrough: { 
        state: 'Not Started',
        subSteps: {
          walkthroughAdditionalInfo: { completed: false, info: null },
          scheduleWalkthrough: { completed: false, scheduleDate: null },
        },
      },
      closingDay: { 
        state: 'Not Started',
        subSteps: {
          closingDayAdditionalInfo: { completed: false, info: null },
        },
      },
  };

  const [nextStepsState, setNextStepsState] = useState(initialState);

// Helper to calculate the overall state from sub-steps:
const calculateMainState = (subSteps) => {
    const steps = Object.values(subSteps);
    const total = steps.length;
    const completedCount = steps.filter(step => step.completed).length;
    if (completedCount === 0) return 'Not Started';
    if (completedCount === total) return 'Completed';
    return 'In Progress';
  };  

    const persistNextStepsState = (state) => {
        // console.log("Persisting next steps state:", state);
        const userId = getUserId(user);
        fetch(`${config.apiBaseUrl}/nextsteps/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ nextsteps: state }),
        })
          .then(response => response.json())
          // .then(data => console.log("Next steps persisted:", data))
          .catch(console.error);
      };

      const updateNextStep = (taskKey, updates, subTaskKey = null) => {
        setNextStepsState(prev => {
          let newState;
          if (subTaskKey) {
            const updatedSubTask = {
              ...prev[taskKey].subSteps[subTaskKey],
              ...updates,
            };
            const updatedSubSteps = {
              ...prev[taskKey].subSteps,
              [subTaskKey]: updatedSubTask,
            };
            // Recalculate the main state from sub-steps
            const mainState = calculateMainState(updatedSubSteps);
            newState = {
              ...prev,
              [taskKey]: {
                subSteps: updatedSubSteps,
                state: mainState,
              },
            };
          } else {
            newState = {
              ...prev,
              [taskKey]: { ...prev[taskKey], ...updates },
            };
          }
          persistNextStepsState(newState);
          return newState;
        });
      };
      

      const toggleMainStepCompletion = (taskKey) => {
        setNextStepsState(prev => {
          let newState;
          if (taskKey === "inspection" || taskKey === "title" || taskKey === "appraisal" || taskKey === "lender" || taskKey === "insurance" || taskKey === "utilities" || taskKey === "closing" || taskKey === "walkthrough" || taskKey === "closingDay" || taskKey === "depositEarnestMoney") {
            // If the main task is not complete, mark all sub-steps as complete;
            // if it is complete, mark them all as not complete.
            const currentTask = prev[taskKey];
            const isCurrentlyCompleted = currentTask.state === "Completed";
            const updatedSubSteps = {};
            Object.keys(currentTask.subSteps).forEach(key => {
              updatedSubSteps[key] = {
                ...currentTask.subSteps[key],
                completed: !isCurrentlyCompleted,
              };
            });
            const newMainState = calculateMainState(updatedSubSteps);
            // Remove any top-level "completed" field if it exists:
            const { completed, ...rest } = currentTask;
            newState = {
              ...prev,
              [taskKey]: {
                ...rest,
                subSteps: updatedSubSteps,
                state: newMainState,
              },
            };
          } else {
            newState = {
              ...prev,
              [taskKey]: { ...prev[taskKey], completed: !prev[taskKey]?.completed },
            };
          }
          persistNextStepsState(newState);
          return newState;
        });
      };      

      const toggleSubStepCompletion = (taskKey, subTaskKey) => {
        const current = nextStepsState[taskKey]?.subSteps?.[subTaskKey]?.completed;
        updateNextStep(taskKey, { completed: !current }, subTaskKey);
      };
      

  const updateSubStep = (mainStep, subStep, updates) => {
    updateNextStep(mainStep, updates, subStep);
  };

  return (
    <NextStepsContext.Provider value={{ nextStepsState, setNextStepsState, updateNextStep, toggleSubStepCompletion, updateSubStep, toggleMainStepCompletion, persistNextStepsState, calculateMainState }}>
      {children}
    </NextStepsContext.Provider>
  );
};

export const useNextSteps = () => useContext(NextStepsContext);
